/**
 * EntityAwareEditor Component
 * Tiptap-based rich text editor with entity detection
 * Now uses project-scoped backend storage via React Query hooks
 */

import { useEffect, useCallback, useRef, useMemo, useState, forwardRef, useImperativeHandle } from 'react'
import { useEditor, EditorContent, Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { EntityMention, DetectedEntity } from './EntityMentionExtension'
import { EntityTypeFilter } from './EntityTypeFilter'
import { Button } from '@/components/ui/button'
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Undo,
  Redo,
  Sparkles,
  Loader2,
  Wand2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useSmartDoc,
  useUpdateSmartDocContent,
  useUpdateEntityStatus,
} from '@/hooks/useSmartDocs'
import { useEntityDetection } from '@/hooks/useEntityDetection'
import { useDocumentGeneration } from '@/hooks/useDocumentGeneration'
import { useKnownEntities, findKnownEntityMatches } from '@/hooks/useKnownEntities'
import { SlashCommandMenu, useSlashCommand } from './SlashCommandMenu'
import { DetectedEntityTooltip } from './DetectedEntityTooltip'
import { EntityMentionTooltip } from './EntityMentionTooltip'
import type { SlashCommand, DetectedEntity as DetectedEntityType } from '@/types/document'
import { mapToDisplayEntityType } from '@/types/document'

// Default visible entity types
const ALL_ENTITY_TYPES = ['Problem', 'Person', 'Decision', 'Risk', 'Opportunity', 'ActionItem', 'Constraint']

interface EntityAwareEditorProps {
  documentId: string
  className?: string
}

export interface EntityAwareEditorHandle {
  scrollToEntity: (entityId: string, evidence: string) => void
  scrollToHeading: (headingText: string) => void
}

export const EntityAwareEditor = forwardRef<EntityAwareEditorHandle, EntityAwareEditorProps>(
  function EntityAwareEditor({ documentId, className }, ref) {
  // React Query hooks for project-scoped data
  const { data: currentDoc } = useSmartDoc(documentId)
  const updateContent = useUpdateSmartDocContent()
  const updateEntityStatus = useUpdateEntityStatus()

  // Local state for entity type visibility (UI-only, not persisted)
  const [visibleEntityTypes, setVisibleEntityTypes] = useState<Set<string>>(new Set(ALL_ENTITY_TYPES))

  const toggleEntityTypeVisibility = useCallback((type: string) => {
    setVisibleEntityTypes(prev => {
      const newSet = new Set(prev)
      if (newSet.has(type)) {
        newSet.delete(type)
      } else {
        newSet.add(type)
      }
      return newSet
    })
  }, [])

  // Tooltip state for detected entity clicks
  const [tooltipEntity, setTooltipEntity] = useState<DetectedEntityType | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null)

  // Tooltip state for confirmed/known entity mention clicks
  const [mentionTooltip, setMentionTooltip] = useState<{
    entityType: string
    entityId?: string | null
    text: string
  } | null>(null)
  const [mentionTooltipPos, setMentionTooltipPos] = useState<{ x: number; y: number } | null>(null)

  const slashCommand = useSlashCommand()
  const editorRef = useRef<Editor | null>(null)
  const prevDocumentIdRef = useRef<string>(documentId)
  const prevStatusesRef = useRef<Map<string, string>>(new Map())

  // Debounce timer for content updates
  const updateTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Initialize editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder: 'Start typing... Use / to insert entities',
      }),
      EntityMention,
      DetectedEntity,
    ],
    content: currentDoc?.content || '<p></p>',
    editorProps: {
      attributes: {
        class: 'prose max-w-none focus:outline-none min-h-[400px] px-12 py-10',
      },
    },
    onUpdate: ({ editor }) => {
      // Debounce content updates to reduce API calls
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current)
      }

      updateTimerRef.current = setTimeout(() => {
        const json = editor.getJSON()
        const text = editor.getText()
        updateContent.mutate({
          id: documentId,
          content: json,
          plainText: text,
        })
      }, 500) // 500ms debounce
    },
  })

  editorRef.current = editor

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current)
      }
    }
  }, [])

  // Sync editor content when document changes
  useEffect(() => {
    if (!editor) return

    // Check if documentId actually changed
    if (prevDocumentIdRef.current !== documentId) {
      prevDocumentIdRef.current = documentId

      // Load the new document's content
      const newContent = currentDoc?.content || { type: 'doc', content: [{ type: 'paragraph' }] }
      editor.commands.setContent(newContent)

      // Clear previous entity status tracking for the new document
      prevStatusesRef.current = new Map()
    }
  }, [editor, documentId, currentDoc?.content])

  // Entity detection
  const plainText = editor?.getText() || ''
  const { status: _status, isAnalyzing, triggerAnalysis } = useEntityDetection({
    documentId,
    content: plainText,
    enabled: true,
    debounceMs: 3000,
  })

  // Known entity registry from project data (personas, competitors, BMC)
  const { data: knownEntities } = useKnownEntities()
  const knownEntitiesAppliedRef = useRef<string>('') // Track which doc we applied to

  // Highlight known entities from project data
  useEffect(() => {
    if (!editor || !knownEntities?.length) return

    const fullText = editor.getText()
    if (!fullText || fullText.length < 10) return

    // Only re-apply if doc content changed significantly
    const contentKey = `${documentId}:${fullText.length}`
    if (knownEntitiesAppliedRef.current === contentKey) return
    knownEntitiesAppliedRef.current = contentKey

    const matches = findKnownEntityMatches(fullText, knownEntities)
    if (!matches.length) return

    for (const match of matches) {
      const index = fullText.indexOf(match.evidence)
      if (index === -1) continue

      const from = index + 1
      const to = from + match.evidence.length

      // Check if already marked
      const { state } = editor
      let alreadyMarked = false
      state.doc.nodesBetween(from, Math.min(to, state.doc.content.size), (node) => {
        if (node.marks.some(mark =>
          mark.type.name === 'entityMention' || mark.type.name === 'detectedEntity'
        )) {
          alreadyMarked = true
        }
      })

      if (!alreadyMarked) {
        const displayType = mapToDisplayEntityType(match.entity.type)
        editor
          .chain()
          .setTextSelection({ from, to })
          .setEntityMention({
            entityType: displayType,
            entityId: match.entity.id,
          })
          .setTextSelection(to)
          .run()
      }
    }
  }, [editor, knownEntities, documentId])

  // AI content generation
  const { generate, isGenerating } = useDocumentGeneration({
    onSuccess: (result) => {
      if (editor && result.tiptapContent) {
        // Update the editor with generated content
        editor.commands.setContent(result.tiptapContent)
        // Also update the backend
        updateContent.mutate({
          id: documentId,
          content: result.tiptapContent,
          plainText: result.plainText,
        })
      }
    },
    onError: (error) => {
      console.error('Generation failed:', error.message || 'Failed to generate content')
    },
  })

  // Check if document has a template (needed for generation)
  const hasTemplate = !!currentDoc?.templateId

  // Handle AI content generation
  const handleGenerate = useCallback(() => {
    if (!hasTemplate || !currentDoc?.templateId) {
      return  // Button is hidden for non-template docs, but guard anyway
    }
    generate({
      docId: documentId,
      templateId: currentDoc.templateId,
      documentTitle: currentDoc.title,
    })
  }, [generate, documentId, hasTemplate, currentDoc?.templateId, currentDoc?.title])

  // Expose scrollToEntity and scrollToHeading via ref
  useImperativeHandle(ref, () => ({
    scrollToEntity: (entityId: string, evidence: string) => {
      if (!editor || !evidence) return

      // Find the evidence text in the document
      const fullText = editor.getText()
      const index = fullText.indexOf(evidence)

      if (index === -1) return

      // Calculate the position (TipTap positions are 1-indexed)
      const from = index + 1
      const to = from + evidence.length

      // Set selection and focus
      editor
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .run()

      // Scroll the selection into view
      const { view } = editor
      const coords = view.coordsAtPos(from)
      const editorElement = view.dom.parentElement

      if (editorElement && coords) {
        // Scroll the editor container to show the selection
        const editorRect = editorElement.getBoundingClientRect()
        const scrollTop = coords.top - editorRect.top - editorElement.offsetHeight / 3
        editorElement.scrollTo({
          top: editorElement.scrollTop + scrollTop,
          behavior: 'smooth',
        })
      }
    },
    scrollToHeading: (headingText: string) => {
      if (!editor) return

      const fullText = editor.getText()
      const index = fullText.indexOf(headingText)

      if (index === -1) return

      const from = index + 1
      const to = from + headingText.length

      editor
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .run()

      // Scroll into view
      const { view } = editor
      const coords = view.coordsAtPos(from)
      const editorElement = view.dom.parentElement

      if (editorElement && coords) {
        const editorRect = editorElement.getBoundingClientRect()
        const scrollTop = coords.top - editorRect.top - 80
        editorElement.scrollTo({
          top: editorElement.scrollTop + scrollTop,
          behavior: 'smooth',
        })
      }
    },
  }), [editor])

  // Get detected entities for highlighting
  const detectedEntities = currentDoc?.detectedEntities || []

  // Compute entity counts by type for filter badges
  const entityCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    detectedEntities.forEach((entity) => {
      // Use mapToDisplayEntityType to ensure consistent type mapping
      const type = mapToDisplayEntityType(entity.type, entity.suggestedOntologyType)
      counts[type] = (counts[type] || 0) + 1
    })
    return counts
  }, [detectedEntities])

  // Highlight detected entities and handle status changes
  useEffect(() => {
    if (!editor) return

    const fullText = editor.getText()
    const prevStatuses = prevStatusesRef.current
    const newStatuses = new Map<string, string>()

    detectedEntities.forEach(entity => {
      const searchText = entity.evidence || entity.content
      if (!searchText || searchText.length < 5) return

      newStatuses.set(entity.id, entity.status)
      const prevStatus = prevStatuses.get(entity.id)

      // Find position of the evidence in the text
      const index = fullText.indexOf(searchText)
      if (index === -1) return

      const from = index + 1
      const to = from + searchText.length

      // If status changed from pending to accepted, convert to entity mention
      if (prevStatus === 'pending' && entity.status === 'accepted') {
        const displayType = mapToDisplayEntityType(entity.type, entity.suggestedOntologyType)
        editor
          .chain()
          .setTextSelection({ from, to })
          .unsetDetectedEntity()
          .setEntityMention({
            entityType: displayType,
          })
          .setTextSelection(to)
          .run()
        return
      }

      // If status changed from pending to rejected, remove highlight
      if (prevStatus === 'pending' && entity.status === 'rejected') {
        editor
          .chain()
          .setTextSelection({ from, to })
          .unsetDetectedEntity()
          .setTextSelection(to)
          .run()
        return
      }

      // If pending and not yet highlighted, add highlight
      if (entity.status === 'pending') {
        const { state } = editor
        let alreadyMarked = false
        state.doc.nodesBetween(from, to, (node) => {
          if (node.marks.some(mark => mark.type.name === 'detectedEntity')) {
            alreadyMarked = true
          }
        })

        if (!alreadyMarked) {
          const displayType = mapToDisplayEntityType(entity.type, entity.suggestedOntologyType)
          editor
            .chain()
            .setTextSelection({ from, to })
            .setDetectedEntity({
              detectedId: entity.id,
              entityType: displayType,
            })
            .setTextSelection(to)
            .run()
        }
      }
    })

    // Update the previous statuses ref
    prevStatusesRef.current = newStatuses
  }, [editor, detectedEntities])

  // Handle slash command detection
  useEffect(() => {
    if (!editor) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Open menu on /
      if (event.key === '/' && !slashCommand.isOpen) {
        const { view } = editor
        const { from } = view.state.selection

        // Get cursor position for menu placement
        const coords = view.coordsAtPos(from)
        // startPos should point to the `/` character itself (from - 1),
        // so textBetween(startPos, from) includes the `/`
        slashCommand.open(
          { top: coords.top, left: coords.left },
          from - 1
        )
        return
      }

      // Update filter while menu is open
      if (slashCommand.isOpen && slashCommand.startPos !== null) {
        const { from } = editor.view.state.selection
        const text = editor.state.doc.textBetween(slashCommand.startPos, from, '')

        // Close if moved away or deleted the /
        if (from < slashCommand.startPos || !text.startsWith('/')) {
          slashCommand.close()
          return
        }

        // Update filter (remove the /)
        slashCommand.updateFilter(text.slice(1))
      }
    }

    // Use capture phase to intercept before Tiptap
    document.addEventListener('keyup', handleKeyDown)
    return () => document.removeEventListener('keyup', handleKeyDown)
  }, [editor, slashCommand])

  // Handle clicks on detected entities and confirmed mentions to show tooltips
  useEffect(() => {
    if (!editor) return

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement

      // Check if clicked on a detected entity highlight (pending)
      const detectedEl = target.closest('.detected-entity-highlight')
      if (detectedEl) {
        setMentionTooltip(null)
        setMentionTooltipPos(null)

        const entityId = detectedEl.getAttribute('data-detected-id')
        if (!entityId) return

        const entity = detectedEntities.find(e => e.id === entityId)
        if (!entity || entity.status !== 'pending') return

        const rect = detectedEl.getBoundingClientRect()
        setTooltipPosition({ x: rect.left, y: rect.bottom })
        setTooltipEntity(entity)
        return
      }

      // Check if clicked on a confirmed entity mention
      const mentionEl = target.closest('.entity-mention')
      if (mentionEl) {
        setTooltipEntity(null)
        setTooltipPosition(null)

        const entityType = mentionEl.getAttribute('data-entity-type') || 'Problem'
        const entityId = mentionEl.getAttribute('data-entity-id')
        const text = mentionEl.textContent || ''

        const rect = mentionEl.getBoundingClientRect()
        setMentionTooltipPos({ x: rect.left, y: rect.bottom })
        setMentionTooltip({ entityType, entityId, text })
        return
      }

      // Close all tooltips if clicking elsewhere
      setTooltipEntity(null)
      setTooltipPosition(null)
      setMentionTooltip(null)
      setMentionTooltipPos(null)
    }

    const editorDom = editor.view.dom
    editorDom.addEventListener('click', handleClick)

    return () => {
      editorDom.removeEventListener('click', handleClick)
    }
  }, [editor, detectedEntities])

  // Handle entity accept/reject via API
  const handleAcceptEntity = useCallback((entityId: string) => {
    updateEntityStatus.mutate({ docId: documentId, entityId, status: 'accepted' })
    setTooltipEntity(null)
    setTooltipPosition(null)
  }, [documentId, updateEntityStatus])

  const handleRejectEntity = useCallback((entityId: string) => {
    updateEntityStatus.mutate({ docId: documentId, entityId, status: 'rejected' })
    setTooltipEntity(null)
    setTooltipPosition(null)
  }, [documentId, updateEntityStatus])

  // Handle command selection
  const handleCommandSelect = useCallback((command: SlashCommand) => {
    if (!editor || slashCommand.startPos === null) return

    const { from } = editor.view.state.selection

    // Delete the slash command text
    editor
      .chain()
      .focus()
      .deleteRange({ from: slashCommand.startPos, to: from })
      .run()

    // Insert entity with proper styling
    const label = prompt(`Enter ${command.name}:`)
    if (label) {
      // Insert text with entityMention mark for visual styling
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          marks: [
            {
              type: 'entityMention',
              attrs: {
                entityType: command.entityType,
              },
            },
          ],
          text: label,
        })
        .insertContent(' ')
        .run()
    }

    slashCommand.close()
  }, [editor, slashCommand])

  if (!editor) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-warm-200 bg-warm-50">
        <ToolbarButton
          icon={Bold}
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        />
        <ToolbarButton
          icon={Italic}
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        />
        <div className="w-px h-5 bg-warm-200 mx-1" />
        <ToolbarButton
          icon={Heading1}
          isActive={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          title="Heading 1"
        />
        <ToolbarButton
          icon={Heading2}
          isActive={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Heading 2"
        />
        <div className="w-px h-5 bg-warm-200 mx-1" />
        <ToolbarButton
          icon={List}
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
        />
        <ToolbarButton
          icon={ListOrdered}
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Ordered List"
        />
        <div className="w-px h-5 bg-warm-200 mx-1" />
        <ToolbarButton
          icon={Undo}
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        />
        <ToolbarButton
          icon={Redo}
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        />

        <div className="flex-1" />

        {/* Detection Status */}
        <div className="flex items-center gap-2 text-xs text-text-muted">
          {isAnalyzing ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Analyzing...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-3 h-3" />
              <span>AI Detection Active</span>
            </>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="ml-2 h-7 text-xs"
          onClick={triggerAnalysis}
          disabled={isAnalyzing}
        >
          <Sparkles className="w-3 h-3 mr-1" />
          Detect Now
        </Button>

        {/* Generate with AI button - only for template-based documents */}
        {hasTemplate && (
          <Button
            variant="default"
            size="sm"
            className="ml-2 h-7 text-xs gap-1.5 bg-business hover:bg-business-dark"
            onClick={handleGenerate}
            disabled={isGenerating}
            title="Generate content with AI"
          >
            {isGenerating ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Wand2 className="w-3 h-3" />
            )}
            {isGenerating ? 'Generating...' : 'Generate with AI'}
          </Button>
        )}
      </div>

      {/* Entity Type Filter */}
      <EntityTypeFilter
        visibleTypes={visibleEntityTypes}
        onToggle={toggleEntityTypeVisibility}
        entityCounts={entityCounts}
      />

      {/* Editor Content */}
      <div
        className={cn(
          'flex-1 overflow-y-auto bg-white',
          // Add hide classes for filtered entity types
          ...Array.from(['Problem', 'Person', 'Decision', 'Risk', 'Opportunity', 'ActionItem', 'Constraint'])
            .filter(type => !visibleEntityTypes.has(type))
            .map(type => `hide-type-${type.toLowerCase()}`)
        )}
      >
        <EditorContent editor={editor} className="h-full" />
      </div>

      {/* Slash Command Menu */}
      <SlashCommandMenu
        isOpen={slashCommand.isOpen}
        position={slashCommand.position}
        filter={slashCommand.filter}
        onSelect={handleCommandSelect}
        onClose={slashCommand.close}
      />

      {/* Detected Entity Tooltip */}
      <DetectedEntityTooltip
        entity={tooltipEntity}
        position={tooltipPosition}
        onAccept={handleAcceptEntity}
        onReject={handleRejectEntity}
        onClose={() => {
          setTooltipEntity(null)
          setTooltipPosition(null)
        }}
      />

      {/* Confirmed/Known Entity Mention Tooltip */}
      <EntityMentionTooltip
        entity={mentionTooltip}
        position={mentionTooltipPos}
        onClose={() => {
          setMentionTooltip(null)
          setMentionTooltipPos(null)
        }}
      />
    </div>
  )
})

// Toolbar Button Component
interface ToolbarButtonProps {
  icon: React.ComponentType<{ className?: string }>
  isActive?: boolean
  disabled?: boolean
  onClick: () => void
  title: string
}

function ToolbarButton({
  icon: Icon,
  isActive,
  disabled,
  onClick,
  title,
}: ToolbarButtonProps) {
  return (
    <button
      className={cn(
        'p-1.5 rounded transition-colors',
        isActive
          ? 'bg-warm-200 text-text-primary'
          : 'text-text-muted hover:bg-warm-100 hover:text-text-secondary',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      <Icon className="w-4 h-4" />
    </button>
  )
}
